import React, { useState } from 'react';
import { ShoppingBag, Package, ShoppingCart, LayoutDashboard, Settings, Users, LogOut, Menu, X, Repeat, Calculator, FileSpreadsheet } from 'lucide-react';
import OrdersSummary from './OrdersSummary';
import AccountSettings from './account/AccountSettings';
import UserManagement from './account/UserManagement';
import StockSync from './account/StockSync';
import CogsRates from './account/CogsRates';
import MonthlyReports from './reports/MonthlyReports';
import { getUser, can, isAdmin } from '../services/session';

const PLATFORMS = [
  { id: 'amazon',  name: 'Amazon',  icon: Package,      color: 'from-orange-500 to-yellow-500', description: 'FBM & FBA Stores' },
  { id: 'shopify', name: 'Shopify', icon: ShoppingBag,  color: 'from-green-500 to-emerald-500', description: 'E-commerce Store' },
  { id: 'ebay',    name: 'eBay',    icon: ShoppingCart, color: 'from-blue-500 to-indigo-500',   description: 'Marketplace Stores' },
  // Ledger temporarily hidden from platform selection — commented out, not removed.
  // { id: 'ledger', name: 'Ledger', icon: BookOpen, color: 'from-purple-500 to-pink-500', description: 'Accounting & Finance' },
];

function Home({ onSelectPlatform }) {
  const platforms = PLATFORMS.filter(p => can(p.id));
  const showSummary = can('summary') || can('sku_mapping');
  return (
    <div className="flex flex-col items-center pb-16">
      <div className="text-center mb-12 mt-4 w-full max-w-6xl">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Centralized Management System</h1>
        <p className="text-xl text-gray-300">{platforms.length ? 'Select your platform to continue' : 'Your account has no platform access yet'}</p>
      </div>

      {platforms.length > 0 && (
        <div className={`grid grid-cols-1 gap-8 w-full max-w-6xl ${platforms.length >= 3 ? 'md:grid-cols-3' : platforms.length === 2 ? 'md:grid-cols-2' : ''}`}>
          {platforms.map((platform) => (
            <button
              key={platform.id}
              onClick={() => onSelectPlatform(platform.id)}
              className="group relative bg-white rounded-2xl p-8 shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${platform.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`} />
              <div className="relative">
                <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${platform.color} rounded-xl mb-6 shadow-lg`}>
                  <platform.icon className="text-white" size={40} />
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">{platform.name}</h2>
                <p className="text-gray-600 mb-6">{platform.description}</p>
                <div className={`inline-flex items-center text-transparent bg-gradient-to-r ${platform.color} bg-clip-text font-semibold gap-2 group-hover:gap-3 transition-all`}>
                  Continue
                  <span className="transform group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showSummary && <OrdersSummary />}

      <p className="text-gray-400 text-sm mt-12">Unified inventory, orders &amp; profitability tracking</p>
    </div>
  );
}

export default function PlatformSelector({ onSelectPlatform, onLogout }) {
  const user = getUser();
  const [section, setSection] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);

  const items = [
    { id: 'home', label: 'Dashboard', icon: LayoutDashboard },
    ...(can('settings') ? [{ id: 'settings', label: 'Settings', icon: Settings }] : []),
    ...(can('reports') ? [{ id: 'reports', label: 'Monthly Reports', icon: FileSpreadsheet }] : []),
    ...(can('cogs') ? [{ id: 'cogs', label: 'COGS Rates', icon: Calculator }] : []),
    ...(can('stock_sync') ? [{ id: 'stock-sync', label: 'Stock Sync', icon: Repeat }] : []),
    ...(isAdmin(user) ? [{ id: 'users', label: 'Users', icon: Users }] : []),
  ];
  const go = (id) => { setSection(id); setMenuOpen(false); };

  const sidebar = (
    <nav className="flex flex-col h-full">
      <div className="px-5 py-6 text-white font-bold text-lg">Management System</div>
      <div className="flex-1 px-3 space-y-1">
        {items.map(it => (
          <button key={it.id} onClick={() => go(it.id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              section === it.id ? 'bg-white/15 text-white' : 'text-gray-300 hover:bg-white/10 hover:text-white'}`}>
            <it.icon size={18} /> {it.label}
          </button>
        ))}
      </div>
      <div className="px-3 pb-5 pt-3 border-t border-white/10">
        <div className="px-3 pb-3">
          <p className="text-white text-sm font-medium truncate">{user?.username}</p>
          <p className="text-gray-400 text-xs">{isAdmin(user) ? 'Admin' : 'User'}</p>
        </div>
        <button onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/10 hover:text-white">
          <LogOut size={18} /> Log out
        </button>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 flex">
      {/* desktop sidebar */}
      <aside className="hidden lg:block w-60 shrink-0 bg-black/30 border-r border-white/10 sticky top-0 h-screen">{sidebar}</aside>

      {/* mobile sidebar */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <aside className="w-64 bg-gray-900 border-r border-white/10 h-full">{sidebar}</aside>
          <button className="flex-1 bg-black/50" onClick={() => setMenuOpen(false)} aria-label="Close menu" />
        </div>
      )}

      <main className="flex-1 min-w-0 p-4 md:p-8">
        <div className="lg:hidden flex items-center justify-between mb-4">
          <button onClick={() => setMenuOpen(o => !o)} className="p-2 text-white rounded-lg hover:bg-white/10" aria-label="Open menu">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <span className="text-white font-semibold">Management System</span>
          <span className="w-9" />
        </div>

        {section === 'home' && <Home onSelectPlatform={onSelectPlatform} />}
        {section === 'settings' && can('settings') && <AccountSettings />}
        {section === 'users' && isAdmin(user) && <UserManagement />}
        {section === 'stock-sync' && can('stock_sync') && <StockSync />}
        {section === 'cogs' && can('cogs') && <CogsRates />}
        {section === 'reports' && can('reports') && <MonthlyReports />}
      </main>
    </div>
  );
}
